import {Banner, Spinner} from '@primer/react'
import {AriaStatus} from '@primer/react/experimental'

import {LABELS} from '../constants/labels'
import type {LowQualityWarningState as LowQualityWarningStateType} from '../hooks/use-low-quality-warning'
import {LowQualityWarningState} from '../hooks/use-low-quality-warning'
import styles from './LowQualityWarningBanner.module.css'

interface LowQualityWarningBannerProps {
  state: LowQualityWarningStateType
  onDismiss: () => void
  onLikeIssue: () => void
  onSubscribe: () => void
  onUnsubscribe: () => void
}

type ActionType = 'like' | 'subscribe' | 'unsubscribe' | 'none'

interface BannerConfig {
  variant: 'warning' | 'success'
  title: string
  message: string
  actionType: ActionType
  isLoading: boolean
}

export function LowQualityWarningBanner({
  state,
  onDismiss,
  onLikeIssue,
  onSubscribe,
  onUnsubscribe,
}: LowQualityWarningBannerProps) {
  const labels = LABELS.lowQualityCommentWarning
  const config = getBannerConfig(state)

  const actionConfig: Record<ActionType, {label: string; onClick: () => void} | null> = {
    like: {label: labels.likeAction, onClick: onLikeIssue},
    subscribe: {label: labels.subscribeAction, onClick: onSubscribe},
    unsubscribe: {label: labels.unsubscribeAction, onClick: onUnsubscribe},
    none: null,
  }
  const action = actionConfig[config.actionType]

  return (
    <Banner
      hideTitle
      variant={config.variant}
      layout="compact"
      title={config.title}
      description={<AriaStatus announceOnShow>{config.message}</AriaStatus>}
      secondaryAction={
        action && (
          <Banner.SecondaryAction onClick={action.onClick} disabled={config.isLoading}>
            <span className={styles.buttonContent}>
              {config.isLoading && <Spinner size="small" />}
              {action.label}
            </span>
          </Banner.SecondaryAction>
        )
      }
      onDismiss={onDismiss}
      flush
      className={styles.banner}
    />
  )
}

function getBannerConfig(state: LowQualityWarningStateType): BannerConfig {
  const labels = LABELS.lowQualityCommentWarning

  switch (state) {
    case LowQualityWarningState.ShowingLikeAction:
      return {variant: 'warning', title: labels.title, message: labels.message, actionType: 'like', isLoading: false}
    case LowQualityWarningState.ShowingSubscribeAction:
      return {
        variant: 'warning',
        title: labels.title,
        message: labels.message,
        actionType: 'subscribe',
        isLoading: false,
      }
    case LowQualityWarningState.ShowingNoAction:
      return {
        variant: 'warning',
        title: labels.title,
        message: labels.noActionsMessage,
        actionType: 'none',
        isLoading: false,
      }
    case LowQualityWarningState.Reacting:
    case LowQualityWarningState.LikedAutoSubscribing:
      // Same UI for both to prevent jitter during like→subscribe flow
      return {variant: 'warning', title: labels.title, message: labels.message, actionType: 'like', isLoading: true}
    case LowQualityWarningState.Subscribing:
      return {
        variant: 'warning',
        title: labels.title,
        message: labels.message,
        actionType: 'subscribe',
        isLoading: true,
      }
    case LowQualityWarningState.Unsubscribing:
      return {
        variant: 'success',
        title: labels.subscribedTitle,
        message: labels.subscribedMessage,
        actionType: 'unsubscribe',
        isLoading: true,
      }
    case LowQualityWarningState.LikedOnly:
      return {
        variant: 'success',
        title: labels.reactedTitle,
        message: labels.reactedMessage,
        actionType: 'none',
        isLoading: false,
      }
    case LowQualityWarningState.LikedAndSubscribed:
      return {
        variant: 'success',
        title: labels.subscribedTitle,
        message: labels.likedAndSubscribedMessage,
        actionType: 'unsubscribe',
        isLoading: false,
      }
    case LowQualityWarningState.Subscribed:
      return {
        variant: 'success',
        title: labels.subscribedTitle,
        message: labels.subscribedMessage,
        actionType: 'unsubscribe',
        isLoading: false,
      }
    case LowQualityWarningState.Unsubscribed:
      return {
        variant: 'success',
        title: labels.unsubscribedTitle,
        message: labels.unsubscribedMessage,
        actionType: 'subscribe',
        isLoading: false,
      }
    case LowQualityWarningState.Resubscribing:
      return {
        variant: 'success',
        title: labels.unsubscribedTitle,
        message: labels.unsubscribedMessage,
        actionType: 'subscribe',
        isLoading: true,
      }
    default:
      return {variant: 'warning', title: labels.title, message: labels.message, actionType: 'none', isLoading: false}
  }
}

try{ LowQualityWarningBanner.displayName ||= 'LowQualityWarningBanner' } catch {}